import React, {useEffect, useRef, useState} from 'react';
import {Card} from "../../../../components/card/card.jsx";
import {BiSolidPencil} from "react-icons/bi";
import {getAssetPath} from "../../../../utils/get-asset-path.js";
import projectUtils from "../../../../utils/project-utils.js";
import AlertMessages from "../../../../components/messages/alert-message.js";
import myUserService from "../../../../services/web/my-user-service.js";
import {toast} from "react-toastify";
import {FaArrowRight} from "react-icons/fa";
import InputSwitch from "../../../../components/input/input-switch.jsx";
import {navigateHook} from "../../../../hooks/use-navigate.js";

const MyUserInfo = ({data, setTarget, refetch}) => {
    const navigate = navigateHook();
    const [imgUrl, setImgUrl] = useState(null);

    const imgRef = useRef(undefined);

    useEffect(() => {
        if (!data?.imageId) return;

        let objectUrl;

        myUserService.getImage(data.imageId)
            .then(blob => {
                objectUrl = URL.createObjectURL(blob);
                setImgUrl(objectUrl);
            })
            .catch(err => {
                console.error("Erro ao carregar imagem:", err);
            });

        return () => {
            if (objectUrl) URL.revokeObjectURL(objectUrl);
        };
    }, [data]);

    const triggerInput = () => imgRef.current?.click();

    const changeUserImage = ({target: {files}}) => {
        const file = files[0];
        if (!file) return;

        if (file.size >= 2000000) {
            let message = 'Tamanho máximo de arquivo excedido. Limite: 2MB.';
            projectUtils.swalErrorMessage('', message, 'Ok');
            imgRef.current.value = null;
            return;
        }

        const formData = new FormData();
        formData.append("file", file);

        myUserService.setImage(formData)
            .then(() => {
                toast.success("Salvo com sucesso");
                refetch();
            })
            .catch(error => {
                console.error(error);

                imgRef.current.value = null;
                const {message} = error.response.data;

                let errMessage = AlertMessages[message] ?? "Não foi possível prosseguir.";
                projectUtils.swalErrorMessage("", errMessage, "Ok");
            });
    }

    const handleChecked = (checked) => {
        const formData = new FormData();
        formData.append("receive_sentry_news", checked);

        myUserService.receiveSentryNews(formData)
            .then(() => {
                toast.success("Salvo com sucesso");
                setTarget("info");
                refetch();
            })
            .catch(error => {
                console.error(error);

                const {message} = error.response.data;

                let errMessage = AlertMessages[message] ?? "Não foi possível prosseguir.";
                projectUtils.swalErrorMessage("", errMessage, "Ok");
            });
    }

    const handleTarget = (id) => {
        if (id === "login") return;

        setTarget(id);
    }

    const infoCards = [
        {id: "login", text: "Login", value: data?.login},
        {id: "name", text: "Nome", value: data?.name},
        {id: "nameWar", text: "Nome de guerra", value: data?.nameWar},
        {id: "email", text: "E-mail", value: data?.email},
        {id: "devices", text: "Contatos", value: data?.devices.length + " contato(s)"},
        {id: "shared", text: "Compartilhamento de dados", value: ""},
        {id: "signature", text: "Assinatura", value: ""},
    ];

    return (
        <>
            <Card>
                <Card.Body className={"!p-[15px]"}>
                    <div className="flex gap-6">
                        <div className="size-20 relative cursor-pointer" onClick={triggerInput}>
                            <div className={'h-[80px] w-[80px] overflow-hidden p-1 bg-gray-100 rounded-full border-[#e0e0e] flex justify-end'} style={{
                                backgroundPosition: "50% center",
                                backgroundSize: "cover",
                                backgroundRepeat: "no-repeat",
                                backgroundImage: imgUrl ? `url(${imgUrl})` : `url(${getAssetPath('img/user.png')}`
                            }}></div>
                            <input type="file" id="img" accept="image/*" onChange={changeUserImage} hidden ref={imgRef}/>
                            <button className={"rounded-full p-0.5 border bg-white border-gray-100 absolute bottom-0.5 right-0.5"}>
                                <BiSolidPencil />
                            </button>
                        </div>
                        <div className={"w-full"}>
                            <h3>{data?.name}</h3>

                            <p className={"truncate !w-[85%]"}><b>Patente:</b> {data?.rank}</p>
                            <p className={"truncate !w-[85%]"}><b>Número funcional:</b> {data?.employeeCode}</p>
                            <p className={"truncate !w-[85%]"} title={data?.groups?.join(", ")}><b>Grupos de operação:</b> {data?.groups?.join(", ")}</p>
                        </div>
                    </div>
                </Card.Body>
            </Card>

            <Card>
                <Card.Body className={"!px-0 !pb-0"}>
                    <h3 className={"px-[15px]"}>Informações pessoais</h3>

                    {
                        infoCards.map(info => (
                            <React.Fragment key={info.id}>
                                <div className={"flex justify-between h-[50px] px-[20px] items-center text-[14px]" + (info.id !== "login" ?  " cursor-pointer" : "")} onClick={() => handleTarget(info.id)}>
                                    {info.text}
                                    <div className="flex justify-end gap-2 items-center">
                                        {info.value}
                                        <FaArrowRight className={info.id !== "login" ? "text-gray-400" : "text-white"}/>
                                    </div>
                                </div>

                                {info.id !== "signature" && <hr className={"opacity-30 !m-0"}/>}
                            </React.Fragment>
                        ))
                    }
                </Card.Body>
            </Card>

            <Card>
                <Card.Body className={"!px-0 !pb-0"}>
                    <h3 className={"px-[15px]"}>Outros</h3>

                    <div className={"flex justify-between h-[50px] px-[20px] items-center text-[14px] cursor-pointer"} onClick={() => navigate("/library")}>
                        Biblioteca
                        <div className="flex justify-end gap-2 items-center">
                            <FaArrowRight className={"text-gray-400"}/>
                        </div>
                    </div>

                    <hr className={"opacity-30 !m-0"}/>

                    <div className={"flex justify-between h-[50px] px-[20px] items-center text-[14px] cursor-pointer"}>
                        Desejo receber novidades do Sentry por e-mail

                        <InputSwitch
                            id="receiveSentryNews"
                            checked={data?.receiveSentryNews ?? false}
                            onChange={handleChecked}
                        />
                    </div>
                </Card.Body>
            </Card>

            <Card>
                <Card.Body className={"!px-0 !pb-0"}>
                    <h3 className={"px-[15px]"}>Acesso e segurança</h3>

                    <div className={"flex justify-between h-[50px] px-[20px] items-center text-[14px] cursor-pointer"} onClick={() => handleTarget("password")}>
                        Alterar senha
                        <div className="flex justify-end gap-2 items-center">
                            {data?.dateChangePassword ? "Alterada em " + new Date(data.dateChangePassword).toLocaleString() : ""}
                            <FaArrowRight className={"text-gray-400"}/>
                        </div>
                    </div>

                    <hr className={"opacity-30 !m-0"}/>

                    <div className={"flex justify-between h-[50px] px-[20px] items-center text-[14px] cursor-pointer"} onClick={() => handleTarget("twoFactor")}>
                        Autenticação de dois fatores
                        <div className="flex justify-end gap-2 items-center">
                            {data?.twoFactorEnabled ? "Ativado" : "Desativado"}
                            <FaArrowRight className={"text-gray-400"}/>
                        </div>
                    </div>

                    <hr className={"opacity-30 !m-0"}/>

                    <div className={"flex justify-between h-[50px] px-[20px] items-center text-[14px] cursor-pointer"} onClick={() => handleTarget("trustedDevices")}>
                        Dispositivos confiáveis
                        <div className="flex justify-end gap-2 items-center">
                            {data?.trustedDevices?.length ?? 0} dispositivos
                            <FaArrowRight className={"text-gray-400"}/>
                        </div>
                    </div>

                    <hr className={"opacity-30 !m-0"}/>

                    <div className={"flex justify-between h-[50px] px-[20px] items-center text-[14px] cursor-pointer"} onClick={() => handleTarget("pin")}>
                        PIN
                        <div className="flex justify-end gap-2 items-center">
                            <FaArrowRight className={"text-gray-400"}/>
                        </div>
                    </div>
                </Card.Body>
            </Card>
        </>
    );
};

export default MyUserInfo;